import React from 'react';
import { useFormikContext } from 'formik';
import { Box, Divider, FormControlLabel, Grid, IconButton, styled, Switch, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker, DatePickerSlotProps } from '@mui/x-date-pickers-pro';
import { startOfDay } from 'date-fns';
import { tz, TZDate } from '@date-fns/tz';

import { TextFormField } from 'lib/form/fields/TextFormField';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { useLocalDateTimeFormat } from 'lib/hooks/useLocalDateTimeFormat';
import { weekdays } from 'components/perks/award-enrichments/consts';
import { AwardRedemptionFullFormData, DateTimeOptionValue } from './PerkRedemptionForm';

const StyledText = styled(Typography)(() => ({
    whiteSpace: 'nowrap'
}));

const StyledSelectFormField = styled(SelectFormField)(() => ({
    width: 'auto'
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.spacing(2),
    fontWeight: theme.typography.fontWeightMedium
}));

interface PerkDateTimeRestrictionSectionProps {
    auto: boolean;
    sectionNumber: string;
    discountValueString: string;
    merchantTimezone?: string;
}

export const PerkDateTimeRestrictionSection: React.FC<PerkDateTimeRestrictionSectionProps> = ({
    auto,
    sectionNumber,
    discountValueString,
    merchantTimezone
}) => {
    const { values, touched, errors, setFieldValue } = useFormikContext<AwardRedemptionFullFormData>();
    const { getUserDateFormat } = useLocalDateTimeFormat();
    const inputFormat = React.useMemo(getUserDateFormat, [getUserDateFormat]);

    const normalizedEndDate = React.useMemo(() => {
        const endDate = values.dateTimeRestriction.endDate;
        return endDate ? startOfDay(new Date(endDate), { in: tz(merchantTimezone || 'UTC') }) : null;
    }, [values.dateTimeRestriction.endDate, merchantTimezone]);

    const normalizedStartDate = React.useMemo(() => {
        const startDate = values.dateTimeRestriction.startDate;
        return startDate ? startOfDay(new Date(startDate), { in: tz(merchantTimezone || 'UTC') }) : null;
    }, [values.dateTimeRestriction.startDate, merchantTimezone]);

    const getDatepickerSlots = React.useCallback(
        (error: string): DatePickerSlotProps<Date, false> => ({
            textField: { variant: 'standard', helperText: error, error: !!error, sx: { mr: 0.5 } }
        }),
        []
    );

    const handleDateFieldChange = React.useCallback(
        (fieldName: string) => (date: Date) => {
            if (!date) {
                setFieldValue(fieldName, undefined);
                return;
            }
            const merchantDate = new TZDate(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                merchantTimezone || 'UTC'
            );
            setFieldValue(fieldName, merchantDate);
        },
        [setFieldValue, merchantTimezone]
    );

    const toggleDateOption = React.useCallback(
        (option: DateTimeOptionValue) => () => {
            const newDateTimeRestriction = { ...values.dateTimeRestriction };

            if (newDateTimeRestriction.dateTimeRestictionOption?.includes(option)) {
                switch (option) {
                    case 'date':
                        newDateTimeRestriction.startDate = undefined;
                        newDateTimeRestriction.endDate = undefined;
                        break;
                    case 'time':
                        newDateTimeRestriction.startTime = undefined;
                        newDateTimeRestriction.endTime = undefined;
                        break;
                    case 'day':
                        newDateTimeRestriction.daysOfWeek = [];
                        break;
                    default:
                        break;
                }
                newDateTimeRestriction.dateTimeRestictionOption =
                    newDateTimeRestriction.dateTimeRestictionOption.filter(value => value !== option);
            } else {
                newDateTimeRestriction.dateTimeRestictionOption = [
                    ...(newDateTimeRestriction.dateTimeRestictionOption ?? []),
                    option
                ];
            }

            setFieldValue('dateTimeRestriction', newDateTimeRestriction);
        },
        [setFieldValue, values.dateTimeRestriction]
    );

    return (
        <>
            {!auto && (
                <Grid size={12}>
                    <Divider />
                </Grid>
            )}
            <Grid size={12}>
                <Box display="flex" flexDirection="column" marginBottom={1.5}>
                    <StyledTitle>{sectionNumber} Setting up specific time / date availability</StyledTitle>
                    <Typography variant="caption">
                        This section controls when an award will be available to the user to redeem in app. If
                        nothing is set, this award will always be available
                    </Typography>
                </Box>
                <Grid container spacing={1.5}>
                    <Grid size={12}>
                        {values.dateTimeRestriction.dateTimeRestictionOption.includes('date') ? (
                            <Box display="flex" alignItems="center">
                                <Box display="flex" alignItems="center" columnGap="4px">
                                    {!auto && (
                                        <StyledText variant="body2">
                                            {discountValueString} will be available between
                                            {''}
                                        </StyledText>
                                    )}
                                    {auto && (
                                        <StyledText variant="body2">
                                            This award will be available between
                                            {''}
                                        </StyledText>
                                    )}
                                    <Box flexShrink={0} width="128px">
                                        <DatePicker
                                            value={normalizedStartDate}
                                            onChange={handleDateFieldChange('dateTimeRestriction.startDate')}
                                            slotProps={getDatepickerSlots(
                                                touched?.dateTimeRestriction?.startDate &&
                                                    errors?.dateTimeRestriction?.startDate
                                            )}
                                            format={inputFormat}
                                        />
                                    </Box>
                                    <StyledText variant="body2"> and </StyledText>
                                    <Box flexShrink={0} width="128px">
                                        <DatePicker
                                            value={normalizedEndDate}
                                            onChange={handleDateFieldChange('dateTimeRestriction.endDate')}
                                            slotProps={getDatepickerSlots(
                                                touched?.dateTimeRestriction?.endDate &&
                                                    errors?.dateTimeRestriction?.endDate
                                            )}
                                            format={inputFormat}
                                        />
                                    </Box>
                                </Box>
                                <IconButton
                                    onClick={toggleDateOption('date')}
                                    aria-label="delete"
                                    size="small"
                                >
                                    <DeleteIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        ) : (
                            <FormControlLabel
                                control={<Switch onChange={toggleDateOption('date')} />}
                                label="Add date range"
                            />
                        )}
                    </Grid>
                    <Grid size={12}>
                        {values.dateTimeRestriction.dateTimeRestictionOption.includes('time') ? (
                            <Box display="flex" alignItems="center">
                                <Box display="flex" alignItems="center" columnGap="4px">
                                    {!auto && (
                                        <StyledText variant="body2">
                                            {discountValueString} will be available between
                                            {''}
                                        </StyledText>
                                    )}
                                    {auto && (
                                        <StyledText variant="body2">
                                            This award will be available between
                                            {''}
                                        </StyledText>
                                    )}
                                    <Box flexShrink={0} width="110px">
                                        <TextFormField
                                            name="dateTimeRestriction.startTime"
                                            type="time"
                                            shrink
                                        />
                                    </Box>
                                    <StyledText variant="body2"> and </StyledText>
                                    <Box flexShrink={0} width="110px">
                                        <TextFormField
                                            name="dateTimeRestriction.endTime"
                                            type="time"
                                            shrink
                                        />
                                    </Box>
                                </Box>
                                <IconButton
                                    onClick={toggleDateOption('time')}
                                    aria-label="delete"
                                    size="small"
                                >
                                    <DeleteIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        ) : (
                            <FormControlLabel
                                control={<Switch onChange={toggleDateOption('time')} />}
                                label="Add time range"
                            />
                        )}
                    </Grid>
                    <Grid size={12}>
                        {values.dateTimeRestriction.dateTimeRestictionOption.includes('day') ? (
                            <Box display="flex" alignItems="center">
                                <Box display="flex" alignItems="center" columnGap="4px">
                                    {!auto && (
                                        <StyledText variant="body2">
                                            {discountValueString} will be available between
                                            {''}
                                        </StyledText>
                                    )}
                                    {auto && (
                                        <StyledText variant="body2">
                                            This award will be available on
                                            {''}
                                        </StyledText>
                                    )}
                                    <Box flexShrink={0}>
                                        <StyledSelectFormField
                                            options={weekdays}
                                            multiple
                                            name="dateTimeRestriction.daysOfWeek"
                                        />
                                    </Box>
                                </Box>
                                <IconButton
                                    onClick={toggleDateOption('day')}
                                    aria-label="delete"
                                    size="small"
                                >
                                    <DeleteIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        ) : (
                            <FormControlLabel
                                control={<Switch onChange={toggleDateOption('day')} />}
                                label="Add specific day(s)"
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};
